import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Shield, Key, ArrowRight, Loader2, AlertCircle, Lock } from 'lucide-react';
import toast from 'react-hot-toast';
import tenantService from '../../services/tenantService';
import { useBranding } from '../../context/BrandingContext';

/**
 * Super Admin Login Page
 * Route: /platform/login
 */
export default function SuperAdminLogin() {
    const [credentials, setCredentials] = useState({
        username: '',
        password: ''
    });
    const branding = useBranding();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();

    // Check if already authenticated
    useEffect(() => {
        const savedToken = localStorage.getItem('token');
        const userType = localStorage.getItem('userType');
        if (savedToken && userType === 'SUPER_USER') {
            navigate('/platform/organizations');
        }
    }, [navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCredentials(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!credentials.username.trim() || !credentials.password.trim()) {
            setError('Please enter both username and password');
            return;
        }

        setLoading(true);

        try {
            // Using axios directly to avoid interceptor conflict during login
            const response = await axios.post(`${import.meta.env.VITE_API_URL || 'http://localhost:8081'}/auth/platform/login`, {
                username: credentials.username.trim(),
                password: credentials.password
            });

            if (response.data && response.data.token) {
                // Store token and user info
                localStorage.setItem('token', response.data.token);
                localStorage.setItem('username', response.data.username);
                localStorage.setItem('userType', 'SUPER_USER'); // Explicitly mark as super user

                toast.success('Super Admin authenticated!');
                navigate('/platform/organizations');
            } else {
                setError('Authentication failed. Invalid response from server.');
            }
        } catch (err) {
            console.error('Auth error:', err);
            const message = err.response?.data?.message || 'Invalid credentials or server error.';
            setError(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
            {/* Background Pattern */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 -left-20 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-indigo-500/5 to-purple-500/5 rounded-full blur-3xl" />
            </div>

            <div className="relative w-full max-w-md">
                {/* Logo/Header */}
                <div className="text-center mb-8">
                    <img
                        src={branding.productLogoUrl}
                        alt={branding.productName}
                        className="w-[8rem] h-[6rem] mx-auto object-contain mb-6"
                    />
                    <h1 className="text-3xl font-black text-white mb-2">Platform Console</h1>
                    <p className="text-slate-400 text-sm">Super Admin • Tenant Management</p>
                </div>

                {/* Login Card */}
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Username Input */}
                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                                Username
                            </label>
                            <div className="relative">
                                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">
                                    <Key size={20} />
                                </div>
                                <input
                                    type="text"
                                    name="username"
                                    value={credentials.username}
                                    onChange={handleChange}
                                    placeholder="Enter username"
                                    className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/20 outline-none transition-all text-sm"
                                    autoFocus
                                    autoComplete="username"
                                />
                            </div>
                        </div>

                        {/* Password Input */}
                        <div>
                            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                                Password
                            </label>
                            <div className="relative">
                                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">
                                    <Lock size={20} />
                                </div>
                                <input
                                    type="password"
                                    name="password"
                                    value={credentials.password}
                                    onChange={handleChange}
                                    placeholder="Enter password"
                                    className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-2xl text-white placeholder-slate-500 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/20 outline-none transition-all text-sm"
                                    autoComplete="current-password"
                                />
                            </div>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-400 text-sm">
                                <AlertCircle size={18} className="shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-4 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white rounded-2xl font-bold flex items-center justify-center gap-3 transition-all shadow-xl shadow-indigo-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <>
                                    <Loader2 size={20} className="animate-spin" />
                                    Authenticating...
                                </>
                            ) : (
                                <>
                                    Access Console
                                    <ArrowRight size={20} />
                                </>
                            )}
                        </button>
                    </form>

                    {/* Security Notice */}
                    <div className="mt-8 pt-6 border-t border-white/10">
                        <div className="flex items-start gap-3 text-xs text-slate-500">
                            <Shield size={16} className="shrink-0 mt-0.5 text-indigo-400" />
                            <p>
                                This console provides system-level access to tenant management operations.
                                All actions are logged for security purposes.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="text-center mt-6">
                    <a
                        href="/login"
                        className="text-sm text-slate-500 hover:text-indigo-400 transition-colors"
                    >
                        ← Back to regular login
                    </a>
                </div>
            </div>
        </div>
    );
}
